<template>
  <b-table class="sticky-table-header" :data="transactions" hoverable>
    <b-table-column
      field="transactions.createdAt"
      :label="$t('transactions.date')"
      v-slot="props"
    >
      <span class="adjust-top">
        {{
          new Date(props.row.createdAt).toLocaleDateString(
            "pt-BR", // default is pt-BR because Brazil, Portugal and United Arab Emirates use the same date format
            transactionDateFormat
          )
        }}
      </span>
    </b-table-column>

    <b-table-column
      field="transactions.store.storeInfo.name"
      :label="$t('transactions.store')"
      v-slot="props"
    >
      <span class="adjust-top">
        {{ props.row.store.storeInfo.name }}
      </span>
    </b-table-column>

    <b-table-column
      field="transactions.user.personalInfo.name"
      :label="$t('transactions.client')"
      v-slot="props"
    >
      <column-user-gou
        v-if="props.row.details.origin === 'gou'"
        :user="props.row.user"
        :invoice-user="props.row.invoice.personalInfo"
      />

      <column-user
        v-else-if="props.row.user"
        :user="props.row.user"
        :invoice-user="props.row.invoice.personalInfo"
        :client-code="props.row.clientCode"
      />

      <p v-else-if="props.row.vending">
        {{ props.row.vending.vendingInfo.name }}
      </p>
    </b-table-column>

    <b-table-column
      field="transactions.cart.totalValue"
      :label="$t('transactions.cart')"
      v-slot="props"
    >
      <span @click.stop="openCartModal(props.row)">
        <column-cart :cart="props.row.cart" :show="!isCartModalActive" />
      </span>
    </b-table-column>

    <b-table-column
      field="transactions.details.status"
      :label="$t('transactions.transaction')"
      v-slot="props"
    >
      <div class="payment-cell">
      <div v-if="props.row.details.provider === 'adyen'">
        <b-tooltip
          v-if="props.row.details.status === 'pending'"
          :label="$t('transactions.qrCodeNotPaidMessage')"
          position="is-left"
          type="is-danger"
        >
          <column-transaction-status-adyen :t="props.row" />
        </b-tooltip>
        <column-transaction-status-adyen v-else :t="props.row" />
      </div>

      <div v-if="props.row.details.provider === 'pagbank'">
        <b-tooltip
          v-if="['pending', 'partial'].includes(props.row.details.status)"
          :label="
            props.row.details.status === 'partial'
              ? $t('transactions.pixCautionaryBlockMessage')
              : $t('transactions.qrCodeNotPaidMessage')
          "
          position="is-left"
          :type="
            props.row.details.status === 'partial' ? 'is-warning' : 'is-danger'
          "
        >
          <column-transaction-status-pagbank :t="props.row" />
        </b-tooltip>
        <column-transaction-status-pagbank v-else :t="props.row" />
      </div>

      <div v-else-if="props.row.details.provider === 'celcoin'">
        <b-tooltip
          v-if="['pending', 'partial'].includes(props.row.details.status)"
          :label="
            props.row.details.status === 'partial'
              ? $t('transactions.pixCautionaryBlockMessage')
              : $t('transactions.qrCodeNotPaidMessage')
          "
          position="is-left"
          :type="
            props.row.details.status === 'partial' ? 'is-warning' : 'is-danger'
          "
        >
          <column-transaction-status-celcoin :t="props.row" />
        </b-tooltip>
        <column-transaction-status-celcoin v-else :t="props.row" />
      </div>

      <column-transaction-status-onii
        v-else-if="props.row.details.provider === 'onii'"
        :t="props.row"
      />

      <column-transaction-status-tef-gsurf
        v-else-if="['tef-gsurf', 'tef'].includes(props.row.details.provider)"
        :t="props.row"
        @mark-as-paid="openMarkAsPaidModal"
      />

      <column-transaction-status-external
        v-else-if="props.row.details.provider === 'external'"
        :t="props.row"
      />

      <column-transaction-status-esitef
        v-else-if="props.row.details.provider === 'esitef'"
        :t="props.row"
      />

      <column-transaction-status-fiserv
        v-else-if="props.row.details.provider === 'tef-fiserv'"
        :t="props.row"
      />

      <column-transaction-status-linx-paykit-tef
        v-else-if="props.row.details.provider === 'linx-paykit-tef'"
        :t="props.row"
        @cancel-transaction="openCancelModal"
      />

      <button
        v-else-if="!props.row.details.provider"
        class="button is-danger"
        disabled
      >
        {{ $t("transactions.status") }}
      </button>
      </div>
    </b-table-column>

    <b-table-column
      field="transactions.invoice.provider"
      :label="$t('transactions.invoice')"
      v-slot="props"
      v-if="!onlyCacauShowVendings"
    >
      <column-sale-status-invoice-express
        v-if="invoiceProvider(props.row.invoice) === 'invoiceExpress'"
        :invoice="props.row.invoice"
        :transactionId="props.row._id"
      />

      <column-sale-status-nfeio
        v-else-if="invoiceProvider(props.row.invoice) === 'nfeio'"
        :transactionId="props.row._id"
        :invoice="props.row.invoice"
        :status="props.row.details.status"
        :merchantReference="
          props.row.details.provider === 'adyen'
            ? (props.row.details.format === 'ecommerce' ||
                props.row.details.format === 'googlepay' ||
                props.row.details.format === 'applepay') &&
              props.row.adyen &&
              props.row.adyen.paymentJSON
              ? props.row.adyen.paymentJSON.reference
              : props.row.details.format === 'terminal' &&
                props.row.adyen &&
                props.row.adyen.paymentJSON &&
                props.row.adyen.paymentJSON.SaleToPOIRequest &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData.SaleTransactionID
              ? props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData.SaleTransactionID.TransactionID
              : null
            : null
        "
        @download-nf="downloadNf"
      />

      <column-sale-status-tecnospeed
        v-else-if="invoiceProvider(props.row.invoice) === 'tecnospeed'"
        :transactionId="props.row._id"
        :invoice="props.row.invoice"
        :status="props.row.details.status"
        :cartItems="props.row.cart.items"
        :merchantReference="
          props.row.details.provider === 'adyen'
            ? (props.row.details.format === 'ecommerce' ||
                props.row.details.format === 'googlepay' ||
                props.row.details.format === 'applepay') &&
              props.row.adyen &&
              props.row.adyen.paymentJSON
              ? props.row.adyen.paymentJSON.reference
              : props.row.details.format === 'terminal' &&
                props.row.adyen &&
                props.row.adyen.paymentJSON &&
                props.row.adyen.paymentJSON.SaleToPOIRequest &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData.SaleTransactionID
              ? props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData.SaleTransactionID.TransactionID
              : null
            : null
        "
        @download-nf="downloadNf"
      />

      <b-dropdown
        v-else
        :triggers="['hover']"
        aria-role="list"
        position="is-bottom-left"
      >
        <template #trigger>
          <button
            class="button"
            :class="invoiceStatusClass(props.row.invoice)"
            slot="trigger"
          >
            {{ invoiceStatusLabel(props.row.invoice) }}
          </button>
        </template>

        <b-dropdown-item aria-role="listitem">
          <b>Provider: </b>-
        </b-dropdown-item>

        <b-dropdown-item aria-role="listitem">
          <b>Status: </b>{{ invoiceStatusLabel(props.row.invoice) }}
        </b-dropdown-item>

        <b-dropdown-item
          @click="copyContent(props.row._id, 'ID da transação copiado!')"
          aria-role="listitem"
        >
          <b>Transaction ID: </b>{{ props.row._id }}
          <b-icon icon="copy"></b-icon>
        </b-dropdown-item>

        <b-dropdown-item
          v-if="props.row.invoice && props.row.invoice.uuid"
          @click="copyContent(props.row.invoice.uuid, 'UUID copiado!')"
          aria-role="listitem"
        >
          <b>UUID: </b>{{ props.row.invoice.uuid }}
          <b-icon icon="copy"></b-icon>
        </b-dropdown-item>

        <b-dropdown-item
          v-if="
            props.row.invoice &&
              props.row.invoice.nfSerie &&
              props.row.invoice.nfNumber
          "
          aria-role="listitem"
        >
          <b>NF: </b
          >{{
            `Nº ${props.row.invoice.nfNumber} | Série ${props.row.invoice.nfSerie}`
          }}
        </b-dropdown-item>
      </b-dropdown>
    </b-table-column>

    <b-table-column v-else :label="$t('transactions.id')" v-slot="props">
      <column-sale-status-cacau
        :transactionId="props.row._id"
        :merchantReference="
          props.row.details.provider === 'adyen'
            ? (props.row.details.format === 'ecommerce' ||
                props.row.details.format === 'googlepay' ||
                props.row.details.format === 'applepay') &&
              props.row.adyen &&
              props.row.adyen.paymentJSON
              ? props.row.adyen.paymentJSON.reference
              : props.row.details.format === 'terminal' &&
                props.row.adyen &&
                props.row.adyen.paymentJSON &&
                props.row.adyen.paymentJSON.SaleToPOIRequest &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData &&
                props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData.SaleTransactionID
              ? props.row.adyen.paymentJSON.SaleToPOIRequest.PaymentRequest
                  .SaleData.SaleTransactionID.TransactionID
              : null
            : null
        "
      />
    </b-table-column>

    <b-table-column
      v-if="gouOrder"
      :label="$t('transactions.status')"
      v-slot="props"
    >
      <column-order-status
        :status="
          props.row.orderLifecycle ? props.row.orderLifecycle.status : null
        "
        :transactionId="props.row._id"
        @get-transactions="$emit('get-transactions')"
      />
    </b-table-column>

    <b-table-column
      v-if="gouOrder"
      :label="$t('transactions.id')"
      v-slot="props"
    >
      {{ props.row._id.substring(20) }}
    </b-table-column>

    <template slot="empty">
      <table-empty-template />
    </template>

    <b-modal :active.sync="isCartModalActive" has-modal-card>
      <cart-modal :transaction="transaction" />
    </b-modal>

    <b-modal :active.sync="isCancelModalActive" has-modal-card>
      <cancel-transaction-modal
        v-if="cancelTransactionTarget"
        :transaction="cancelTransactionTarget"
        @cancelled="onTransactionCancelled"
      />
    </b-modal>

    <b-modal :active.sync="isMarkAsPaidModalActive" has-modal-card>
      <tef-mark-as-paid-modal
        v-if="markAsPaidTransactionTarget"
        :transaction="markAsPaidTransactionTarget"
        @marked-as-paid="onTransactionMarkedAsPaid"
      />
    </b-modal>
  </b-table>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../../mixins/common";
import columnUser from "./ColumnUser";
import columnUserGou from "./ColumnUserGou";
import columnCart from "./ColumnCart";
import columnTransactionStatusEsitef from "./ColumnTransactionStatuseSitef";
import columnTransactionStatusOnii from "./ColumnTransactionStatusOnii";
import columnTransactionStatusAdyen from "./ColumnTransactionStatusAdyen";
import columnTransactionStatusPagbank from "./ColumnTransactionStatusPagbank";
import columnTransactionStatusExternal from "./ColumnTransactionStatusExternal";
import columnTransactionStatusTefGsurf from "./ColumnTransactionStatusTefGsurf";
import columnTransactionStatusFiserv from "./ColumnTransactionStatusFiserv.vue";
import columnTransactionStatusCelcoin from "./ColumnTransactionStatusCelcoin.vue";
import columnSaleStatusCacau from "./ColumnSaleStatusCacau";
import columnSaleStatusInvoiceExpress from "./ColumnSaleStatusInvoiceExpress";
import columnOrderStatus from "./ColumnOrderStatus";
import columnSaleStatusNfeio from "./ColumnSaleStatusNfeio";
import columnSaleStatusTecnospeed from "./ColumnSaleStatusTecnospeed";
import tableEmptyTemplate from "../../common/TableEmptyTemplate";
import cartModal from "./CartModal.vue";
import columnTransactionStatusLinxPaykitTef from "./ColumnTransactionStatusLinxPaykitTef.vue";
import cancelTransactionModal from "./CancelTransactionModal.vue";
import tefMarkAsPaidModal from "./TefMarkAsPaidModal.vue";

export default {
  props: ["transactions", "gouOrder"],

  mixins: [common],

  components: {
    columnUser,
    columnUserGou,
    columnCart,
    columnTransactionStatusEsitef,
    columnTransactionStatusOnii,
    columnTransactionStatusAdyen,
    columnTransactionStatusPagbank,
    columnTransactionStatusExternal,
    columnTransactionStatusTefGsurf,
    columnTransactionStatusFiserv,
    columnTransactionStatusCelcoin,
    columnTransactionStatusLinxPaykitTef,
    columnSaleStatusCacau,
    columnSaleStatusInvoiceExpress,
    columnOrderStatus,
    columnSaleStatusNfeio,
    columnSaleStatusTecnospeed,
    tableEmptyTemplate,
    cartModal,
    cancelTransactionModal,
    tefMarkAsPaidModal
  },

  data() {
    return {
      transactionDateFormat: {
        year: "2-digit",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric"
      },
      interval: null,
      isCartModalActive: false,
      transaction: null,
      isCancelModalActive: false,
      cancelTransactionTarget: null,
      isMarkAsPaidModalActive: false,
      markAsPaidTransactionTarget: null
    };
  },

  computed: {
    ...mapGetters(["onlyCacauShowVendings"])
  },

  methods: {
    openCancelModal(t) {
      this.cancelTransactionTarget = t;
      this.isCancelModalActive = true;
    },

    onTransactionCancelled() {
      this.$emit("get-transactions");
    },

    openMarkAsPaidModal(t) {
      this.markAsPaidTransactionTarget = t;
      this.isMarkAsPaidModalActive = true;
    },

    onTransactionMarkedAsPaid() {
      this.$emit("get-transactions");
    },

    invoiceProvider(invoice) {
      return invoice && invoice.provider ? invoice.provider : null;
    },

    invoiceStatusLabel(invoice) {
      if (!invoice || !invoice.status) return "-";

      return invoice.status === "notSent"
        ? "Não enviada"
        : invoice.status === "emitted"
        ? "Emitida"
        : invoice.status === "rejected"
        ? "Rejeitada"
        : invoice.status === "pending"
        ? "Pendente"
        : invoice.status === "error"
        ? "Erro"
        : invoice.status === "cancelled"
        ? "Cancelada"
        : invoice.status === "notEnabled"
        ? "Não habilitada"
        : invoice.status === "ncm-missing"
        ? "Faltando NCM"
        : invoice.status === "nf-serie-not-configured"
        ? "Faltando número de série"
        : invoice.status === "unusedNumber"
        ? "Inutilizada"
        : invoice.status === "notApplicable"
        ? "Não aplicável"
        : invoice.status;
    },

    invoiceStatusClass(invoice) {
      if (!invoice) return {};

      return {
        "is-success": invoice.status === "emitted",
        "is-danger": [
          "rejected",
          "error",
          "ncm-missing",
          "nf-serie-not-configured"
        ].includes(invoice.status),
        "is-dark": ["cancelled", "unusedNumber"].includes(invoice.status),
        "is-warning": invoice.status === "pending",
        "is-info": invoice.status === "notApplicable"
      };
    },

    downloadNf(e) {
      const transaction = this.transactions.find(t => {
        return t._id == e.transactionId;
      });

      if (transaction) {
        this.$emit("loading", true);

        this.$store
          .dispatch("getInvoice", {
            transactionId: transaction._id,
            documentType: e.type
          })
          .then(resp => {
            if (resp.data.result === "ok") {
              if (resp.data.payload.exists) {
                const link = document.createElement("a");
                link.download = "";
                link.href = resp.data.payload.url;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            } else {
              this.alertError(resp.data);
            }
          })
          .catch(() => {
            this.alertError({ message: "UNEXPECTED_ERROR" });
          })
          .finally(() => {
            this.$emit("loading", false);
          });
      }
    },
    openCartModal(transaction) {
      this.transaction = transaction;
      this.isCartModalActive = true;
    }
  },

  mounted() {
    clearInterval(this.interval);
    if (this.gouOrder) {
      this.interval = setInterval(() => {
        this.$emit("get-transactions", true);
      }, 10000);
    }
  },

  destroyed() {
    clearInterval(this.interval);
  }
};
</script>

<style>
.adjust-top {
  position: relative;
  top: 8px;
}

.payment-cell {
  position: relative;
  display: inline-block;
}
</style>
